import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MessageCircle, Clock, ArrowUpRight, Sparkles, Send, Inbox, Lock, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

function windowText(w) {
  if (!w) return 'Loading…';
  if (w.open) {
    const hrs = Math.floor((w.seconds_remaining || 0) / 3600);
    const mins = Math.floor(((w.seconds_remaining || 0) % 3600) / 60);
    return hrs >= 1 ? `Window open · ${hrs}h ${mins}m left` : `Window open · ${mins}m left`;
  }
  return w.reason === 'no_inbound' ? 'No inbound yet — needs a template first' : '24h window closed';
}

export default function WhatsAppLeadSummary({ leadId, onSendTemplate }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/whatsapp/conversation/${leadId}`);
      setData(res.data);
    } catch { /* non-blocking */ }
  }, [leadId]);

  useEffect(() => { if (leadId) load(); }, [leadId, load]);

  const open = data?.window?.open;
  const draft = data?.pending_draft;
  const humanNeeded = data?.human_needed;

  return (
    <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.03] p-3.5 mb-3" data-testid="wa-lead-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10"><MessageCircle className="h-4 w-4 text-emerald-500" /></div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">WhatsApp</span>
              <Badge variant="outline" className={`text-[10px] ${open ? 'border-emerald-500/40 text-emerald-500' : 'border-zinc-600 text-zinc-400'}`} data-testid="wa-summary-window">
                <Clock className="h-2.5 w-2.5 mr-0.5" />{windowText(data?.window)}
              </Badge>
              {draft && (
                <Badge variant="outline" className="text-[10px] border-violet-500/40 text-violet-400">
                  <Sparkles className="h-2.5 w-2.5 mr-0.5" />Draft ready
                </Badge>
              )}
              {humanNeeded && (
                <Badge variant="outline" className="text-[10px] border-red-500/50 text-red-400 bg-red-500/10" data-testid="wa-human-needed-badge">
                  <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />AI paused — human needed
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {data ? `${data.outbound_count || 0} sent · ${data.inbound_count || 0} received` : '—'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => navigate(`/whatsapp?tab=conversations&lead=${leadId}`)} data-testid="wa-open-inbox-btn">
            <Inbox className="h-3.5 w-3.5 mr-1" />Open in Inbox<ArrowUpRight className="h-3 w-3 ml-0.5" />
          </Button>
          {!open && (
            <Button size="sm" variant="outline" onClick={onSendTemplate} data-testid="wa-send-template-btn">
              <Send className="h-3.5 w-3.5 mr-1" />Send template
            </Button>
          )}
        </div>
      </div>
      {!open && (
        <p className="text-[11px] text-amber-300/80 mt-2.5 flex items-center gap-1.5" data-testid="wa-summary-closed-note">
          <Lock className="h-3 w-3" />Free-text replies are blocked outside Meta's 24h window. Send a pre-approved template to re-open the conversation.
        </p>
      )}
      {humanNeeded && (
        <p className="text-[11px] text-red-300/90 mt-2.5 flex items-center gap-1.5" data-testid="wa-human-needed-note">
          <AlertTriangle className="h-3 w-3" />
          {humanNeeded.reason === 'loop_guard'
            ? 'Auto-reply paused: the AI sent several replies without progress. Jump in and take over this thread.'
            : 'Auto-reply paused: this lead was handed off for a human to respond.'}
        </p>
      )}
    </div>
  );
}
